import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { blogService } from '../services/blogService';
import { Button } from '../components/ui/button';
import OptimizedImage from '../components/OptimizedImage';
import CommentSection from '../components/blog/CommentSection';
import SEO from '../components/SEO';

const BlogDetail = () => {
    const { id: slug } = useParams(); // 'id' in route is actually the slug
    const [blog, setBlog] = useState(null);
    const [relatedBlogs, setRelatedBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchBlog();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const fetchBlog = async () => {
        try {
            setLoading(true);
            const response = await blogService.getBySlug(slug);
            setBlog(response.data.post);
            setRelatedBlogs(response.data.relatedPosts || []);
        } catch (err) {
            console.error('Error fetching blog:', err);
            setError(err.response?.status === 404 ? 'Blog not found' : 'Failed to load blog');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white pt-32 pb-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-xl" style={{ color: '#31415A' }}>Loading...</p>
                </div>
            </div>
        );
    }

    if (error || !blog) {
        return (
            <div className="min-h-screen bg-white pt-32 pb-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-4xl font-bold mb-4" style={{ color: '#31415A' }}>
                        {error || 'Blog Not Found'}
                    </h1>
                    <Link to="/blogs">
                        <Button style={{ backgroundColor: '#31415A', color: '#FFFFFF' }}>
                            Back to Blogs
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pt-32 pb-20">
            {blog && (
                <SEO
                    title={blog.title}
                    description={blog.excerpt}
                    image={blog.image}
                    type="article"
                />
            )}
            <div className="max-w-4xl mx-auto px-6 sm:px-6 lg:px-8">
                {/* Back Button */}
                <Link
                    to="/blogs"
                    className="inline-flex items-center gap-2 mb-8 text-gray-600 hover:text-gray-900 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>Back to Blogs</span>
                </Link>

                {/* Category Badge */}
                {blog.category && (
                    <div className="mb-4">
                        <span
                            className="inline-block px-4 py-1 text-sm font-medium text-white rounded-full"
                            style={{ backgroundColor: '#C9A14F' }}
                        >
                            {blog.category}
                        </span>
                    </div>
                )}

                {/* Content Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-12">
                    {/* Featured Image */}
                    {blog.image && (
                        <div className="w-full h-[400px] md:h-[500px]">
                            <OptimizedImage
                                src={blog.image}
                                alt={blog.title}
                                className="w-full h-full"
                                style={{ objectFit: 'cover' }}
                                loading="eager"
                            />
                        </div>
                    )}

                    <div className="p-8 md:p-12">
                        {/* Title */}
                        <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight" style={{ color: '#31415A' }}>
                            {blog.title}
                        </h1>

                        {/* Date & Meta */}
                        <div className="flex items-center gap-4 text-gray-500 mb-8 border-b border-gray-100 pb-8">
                            <span style={{ color: '#C9A14F', fontWeight: 'bold' }}>
                                {new Date(blog.published_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </span>
                            <span>•</span>
                            <span>{blog.author || 'Iraga Team'}</span>
                        </div>

                        {/* Article Content */}
                        <div className="prose prose-lg max-w-none text-gray-700 text-sm md:text-base">
                            {blog.excerpt && (
                                <p className="text-xl leading-relaxed mb-6 font-medium text-gray-800">
                                    {blog.excerpt}
                                </p>
                            )}

                            <div
                                dangerouslySetInnerHTML={{ __html: blog.content }}
                            />
                        </div>
                    </div>
                </div>


                {/* Comment Section */}
                <CommentSection
                    postSlug={blog.slug}
                    allowComments={blog.allow_comments !== false}
                />


                {/* Related Posts */}
                {relatedBlogs.length > 0 && (
                    <div className="mt-10">
                        <h3 className="text-3xl font-bold mb-8" style={{ color: '#31415A' }}>
                            Related Articles
                        </h3>
                        <div className="grid md:grid-cols-2 gap-6">
                            {relatedBlogs.map((relatedBlog) => (
                                <Link
                                    key={relatedBlog.id}
                                    to={`/blogs/${relatedBlog.slug}`}
                                    className="group border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
                                >
                                    <div className="h-[200px] overflow-hidden">
                                        <OptimizedImage
                                            src={relatedBlog.image || '/images/placeholder-blog.jpg'}
                                            alt={relatedBlog.title}
                                            className="w-full h-full"
                                            style={{ objectFit: 'cover' }}
                                        />
                                    </div>
                                    <div className="p-6">
                                        <p className="text-sm mb-2" style={{ color: '#5C5935' }}>
                                            {new Date(relatedBlog.published_at).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </p>
                                        <h4 className="text-lg font-semibold" style={{ color: '#31415A' }}>
                                            {relatedBlog.title}
                                        </h4>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogDetail;
